import { useStore } from '@nanostores/react';
import { ClientOnly } from 'remix-utils/client-only';
import { chatStore } from '~/lib/stores/chat';
import { classNames } from '~/utils/classNames';
import { ChatDescription } from '~/lib/persistence/ChatDescription.client';
import { Menu } from '~/components/sidebar/Menu.client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { projectId, description, db, deleteByProjectId, getFramework } from '~/lib/persistence';
import { clearProjectRules } from '~/lib/stores/rules';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/remix';

// trying out
import { workbenchStore, type WorkbenchViewType } from '~/lib/stores/workbench';
import { Slider, type SliderOptions } from '~/components/ui/Slider';
import { computed } from 'nanostores';
import { useNavigate, useLocation, useLoaderData } from '@remix-run/react';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { Headset, LogOut, Menu as MenuIcon, X, MessageCircle, Mail, ChevronDown, BookOpen } from 'lucide-react';
import { ProUpgradeModal } from '~/components/ui/ProUpgradeModal';
import { userStore } from '~/lib/stores/user';
import { gemini_flash, claude_sonnet_4, isDev, vertex_claude_sonnet_4_5 } from '~/utils/constants';

// New imports for Storage Management
import { StorageManagementButton } from './StorageManagementButton.client';
import { StorageExceededModal } from '~/components/modals/StorageExceededModal.client';
import { frameworkService } from '~/lib/services/frameworkService';
import {
  techStackSelectionStore,
  setFramework,
  setLanguage,
  type Framework,
  type Language,
} from '~/lib/stores/techStackSelection';
import { planNameCleaner } from '~/lib/utils/planNameCleaner';
import { isMobile } from '~/utils/mobile';
import ButtonWithDropdown from '~/components/ui/ButtonWithDropdown';
import { type MenuLink } from '~/components/ui/ButtonWithDropdown';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { useStorageStatus } from '~/hooks/useStorageStatus';

// Backend logout API call
export async function callLogoutAPI() {
  try {
    return await Sentry.startSpan(
      {
        name: 'callLogoutAPI',
        op: 'callLogoutAPI',
      },
      async () => {
        const response = await fetch('/auth/logout', {
          method: 'POST',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Logout API failed with status: ${response.status}`);
        }

        const result = await response.json();
        console.log('[Header] Logout API response:', result);
        return result;
      },
    );
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        message: 'Error calling logout API',
      },
    });
    console.error('[Header] Error calling logout API:', error);
    throw error;
  }
}

// This will be created dynamically with translations inside the component

const formatTechStack = (framework: Framework | undefined, language: Language | undefined) => {
  if (!framework || !language) {
    return '';
  }

  const frameworkDisplay = frameworkService.formatFramework(framework);
  const languageDisplay = frameworkService.formatLanguage(language);

  return `${frameworkDisplay} + ${languageDisplay}`;
};

interface HeaderProps {
  paymentUrl: string;
}

export function Header({ paymentUrl }: HeaderProps) {
  const { t } = useTranslation();
  const chat = useStore(chatStore);
  const [techStack, setTechStack] = useState<{ framework: string; language: string } | null>(null);

  // Create slider options with translations
  const sliderOptions: SliderOptions<WorkbenchViewType> = {
    left: {
      value: 'code',
      text: t('header.code'),
    },
    right: {
      value: 'preview',
      text: t('header.preview'),
    },
  };
  const [open, setOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [dbReady, setDbReady] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  console.log('header paymentUrl', paymentUrl);

  const menuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const userData = useStore(userStore);
  const location = useLocation();
  const prevLocationRef = useRef(location);

  const selectedView = useStore(workbenchStore.currentView);
  const hasPreview = useStore(computed(workbenchStore.previews, (previews) => previews.length > 0));
  const setSelectedView = (view: WorkbenchViewType) => {
    workbenchStore.currentView.set(view);
    workbenchStore.setDeviceView('desktop');
  };

  const [isProUpgradeModalOpen, setIsProUpgradeModalOpen] = useState(false);

  const aiModel = useStore(selectionStore).aiModel;
  let model;

  if (aiModel === 'dev-ai-model') {
    model = gemini_flash;
  } else if (aiModel === 'dev-mock-ai-model') {
    model = 'Mock AI Call';
  } else {
    model = vertex_claude_sonnet_4_5;
  }

  // Check if user has exhausted all prompts
  const areAllPromptsUsed = userData.promptsUsed === userData.maxPrompts;

  const { allProjects } = useStorageStatus();

  useEffect(() => {
    if (location.pathname.includes('/chat')) {
      setIsChatOpen(true);
    } else {
      setIsChatOpen(false);
    }

    prevLocationRef.current = location;
  }, [location]);

  useEffect(() => {
    if (hasPreview) {
      setSelectedView('preview');
    }
  }, [hasPreview]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }

      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Check if IndexedDB is ready
    if (db) {
      setDbReady(true);
    }
  }, []);

  useEffect(() => {
    const getCurrentTechStack = async () => {
      const currentProjectId = projectId.get();
      console.log('[TechStack Debug] Fetching tech stack. Trigger params:', {
        dbReady,
        currentProjectId,
        isChatOpen,
        pathname: location.pathname,
        trigger: 'location change',
      });

      if (!db || !currentProjectId) {
        const framework = techStackSelectionStore.get().framework;
        const language = techStackSelectionStore.get().language;
        console.log('[TechStack Debug] No DB/ChatId - Using framework store:', { framework, language });
        setTechStack({ framework, language });
        setFramework(framework as Framework);
        setLanguage(language as Language);
        return;
      }

      try {
        console.log('[TechStack Debug] Fetching framework from DB for chatId:', currentProjectId);

        const result = await getFramework(db, currentProjectId);
        console.log('[TechStack Debug] DB Result:', result);

        if (result) {
          const { framework, language } = result;

          if (framework && language && framework !== 'unknown' && language !== 'unknown') {
            console.log('[TechStack Debug] Using valid DB values:', { framework, language });
            setTechStack({ framework, language });
            setFramework(framework as Framework);
            setLanguage(language as Language);
          } else {
            const framework = techStackSelectionStore.get().framework;
            const language = techStackSelectionStore.get().language;
            console.log('[TechStack Debug] DB values unknown - Using framework store:', { framework, language });
            setTechStack({ framework, language });
            setFramework(framework as Framework);
            setLanguage(language as Language);
          }
        } else {
          const framework = techStackSelectionStore.get().framework;
          const language = techStackSelectionStore.get().language;
          console.log('[TechStack Debug] No DB result - Using framework store:', { framework, language });
          setTechStack({ framework, language });
          setFramework(framework as Framework);
          setLanguage(language as Language);
        }
      } catch (error) {
        console.error('[TechStack Debug] Error fetching framework:', error);

        const framework = techStackSelectionStore.get().framework;
        const language = techStackSelectionStore.get().language;
        console.log('[TechStack Debug] Error fallback - Using framework store:', { framework, language });
        setTechStack({ framework, language });
        setFramework(framework as Framework);
        setLanguage(language as Language);
      }
    };

    console.log('[TechStack Debug] Effect triggered with pathname:', location.pathname);
    getCurrentTechStack();
  }, [
    dbReady,
    projectId.get(),
    isChatOpen,
    techStackSelectionStore.get().framework,
    techStackSelectionStore.get().language,
  ]);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Reset chat state when clicking the logo to start a new chat
  const handleLogoClick = (e: React.MouseEvent) => {
    // Reset chatId to undefined for new chat
    const oldProjectId = projectId.get();
    console.log('[header] Resetting chat state. Old projectId:', oldProjectId);

    projectId.set(undefined);
    description.set(undefined);

    // Verify the chatId was reset
    console.log('[header] projectId after reset:', projectId.get());

    // Clear project rules for new chat using the dedicated function
    clearProjectRules();
    console.log('[header] Project rules reset for new chat');

    // Let the default link behavior continue
  };

  const [isStorageExceededModalOpen, setIsStorageExceededModalOpen] = useState(false);

  // Logout function
  const handleLogout = useCallback(async () => {
    const currentUser = userData.email;
    console.log('[Header] Logout initiated by user:', currentUser);

    try {
      // Call backend logout API to clear server-side session
      console.log('[Header] Calling backend logout API');
      await callLogoutAPI();

      // Clear user data from store
      console.log('[Header] Clearing user data from store');
      userStore.set({
        email: '',
        isSubscribed: false,
        maxPrompts: '0',
        promptsUsed: '0',
        planName: 'Free',
        name: '',
        inviteCode: '',
        topUpPrompts: '0',
        topUpPromptsUsed: '0',
        created_at: new Date(),
      });
      console.log('[Header] User data cleared from store');

      // Clear authentication token from localStorage
      console.log('[Header] Clearing authentication tokens from localStorage');

      const hasSupabaseToken = localStorage.getItem('supabase_access_token');

      // Clear all localStorage data except promptParam
      console.log('[Header] Clearing all localStorage data except promptParam');

      const promptParam = localStorage.getItem('promptParam');

      // Clear all localStorage
      localStorage.clear();

      // Restore promptParam if it existed
      if (promptParam) {
        localStorage.setItem('promptParam', promptParam);
        console.log('[Header] promptParam preserved in localStorage');
      }

      if (hasSupabaseToken) {
        localStorage.removeItem('supabase_access_token');
        console.log('[Header] Supabase token removed from localStorage');
      }

      // Clear all sessionStorage data for perfect logout
      console.log('[Header] Clearing all sessionStorage data');
      sessionStorage.clear();

      toast.success('Successfully logged out');

      // Redirect to onboarding or home page
      console.log('[Header] Redirecting to onboarding page');
      navigate('/onboarding');

      console.log('[Header] Logout process completed successfully for user:', currentUser);
    } catch (error) {
      console.error('[Header] Error during logout process:', error);
      toast.error('Error occurred during logout');

      // Still attempt to redirect even if there was an error
      navigate('/onboarding');
    }
  }, [navigate, userData.email]);

  // TODO: Return null if route is onboarding
  if (pathname.includes('/onboarding') || pathname.includes('/pricing')) {
    return null;
  }

  const links: MenuLink[] = [
    {
      type: 'link',
      icon: <Mail className="w-4 h-4" />,
      title: t('header.emailUs'),
      url: 'mailto:info@dualite.dev',
    },

    {
      type: 'link',
      icon: <Headset className="w-4 h-4" />,
      title: t('header.scheduleCall'),
      url: 'https://calendly.com/singhvirohan/dualite-feedback',
    },
  ];

  return (
    <header
      style={{
        background: 'rgba(0, 0, 0, 0)',
      }}
      className={classNames(
        'flex items-center space-between bg-alpha-elements-background-depth-1 pl-1 pt-3 h-[var(--header-height)]',
        {
          'border-transparent w-full gap-5 pr-2': !chat.started,
          'border-alpha-elements-borderColor w-full': chat.started,
        },
      )}
    >
      <div className="flex flex-1 items-center gap-2">
        <div
          ref={menuRef}
          className="ml-2 flex items-center gap-2 z-logo text-alpha-elements-textPrimary cursor-pointer transition-all"
        >
          <ClientOnly>{() => <Menu open={open} onClose={() => setOpen(false)} projects={allProjects} />}</ClientOnly>

          <a href="/" className="text-xl font-semibold text-accent flex items-center" onClick={handleLogoClick}>
            {/* <span className="i-alpha:logo-text?mask w-[46px] inline-block" /> */}
            <img src="/images/dualite-logo.svg" alt="logo" className="w-[40px] inline-block mx-2" />
          </a>
          {chat.started ? (
            <span className="flex-1 px-2 truncate text-center text-alpha-elements-textPrimary">
              <ClientOnly>{() => <ChatDescription />}</ClientOnly>
            </span>
          ) : null}
          <div
            className="i-ph:sidebar-simple-duotone text-xl"
            onClick={() => {
              setOpen(!open);
            }}
          />
          {chat.started && (
            <div className="px-2 py-1 rounded-md bg-zinc-800/90 text-sm font-medium text-gray-200 border border-zinc-700/50 hidden sm:block">
              {formatTechStack(techStack?.framework as Framework, techStack?.language as Language)}
            </div>
          )}
        </div>

        {chat.started && (
          <div className="absolute left-1/2 -translate-x-1/2 z-[1000]">
            {isMobile() && !chat.showChat ? (
              <div className="absolute flex w-full justify-center mt-6">
                <Slider selected={selectedView} options={sliderOptions} setSelected={setSelectedView} />
              </div>
            ) : !isMobile() ? (
              <Slider selected={selectedView} options={sliderOptions} setSelected={setSelectedView} />
            ) : null}
          </div>
        )}

        {isDev && (
          <div className="ml-6 w-fit text-sm text-alpha-elements-textSecondary hidden md:block">Model: {model}</div>
        )}
      </div>

      <div className="flex gap-2">
        {/* Desktop version - hide on mobile */}

        <div className="hidden md:flex gap-2">
          {/* Only show subscription status once we have user data */}
          {userData.email !== 'user_email@dummy.dualite' &&
            window.location.pathname !== '/billing' &&
            !window.location.pathname.includes('/chat') && (
              <>
                {userData.isSubscribed && areAllPromptsUsed ? (
                  <div
                    className="cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md whitespace-nowrap font-semibold flex items-center justify-center"
                    onClick={() => {
                      setIsProUpgradeModalOpen(true);
                    }}
                  >
                    {t('header.exhausted')}
                  </div>
                ) : userData.isSubscribed ? (
                  <div className="cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md whitespace-nowrap font-semibold flex items-center justify-center">
                    {t('header.planUser', { planName: planNameCleaner(userData.planName).planName })}
                  </div>
                ) : (
                  <div
                    className="cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md whitespace-nowrap font-semibold flex items-center justify-center"
                    onClick={() => {
                      setIsProUpgradeModalOpen(true);
                    }}
                  >
                    {t('header.upgradeToPro')}
                  </div>
                )}
              </>
            )}

          {!chat.started && (
            <>
              {/* Tutorial Button */}
              <a
                href="https://alphadocs.dualite.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-3 rounded-full text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md whitespace-nowrap font-semibold"
              >
                <BookOpen className="w-4 h-4" />
                Tutorial
              </a>

              {/* Support Dropdown */}
              <ButtonWithDropdown
                links={links}
                openIcon={
                  <div className="flex items-center gap-2">
                    Support
                    <ChevronDown className="w-4 h-4" />
                  </div>
                }
                closedIcon={
                  <div className="flex items-center gap-2">
                    Support
                    <ChevronDown className="w-4 h-4" />
                  </div>
                }
                position="flex"
                buttonClassName="cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-3 rounded-full text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md whitespace-nowrap font-semibold"
              />
            </>
          )}
          {/* Storage Management Button will go here */}
          <ClientOnly>
            {() => <StorageManagementButton setIsStorageExceededModalOpen={setIsStorageExceededModalOpen} />}
          </ClientOnly>

          {/* setting icon */}
          <button
            onClick={() => {
              navigate('/settings');
            }}
            className="flex items-center gap-2 cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md whitespace-nowrap font-semibold"
          >
            <i className="i-ph:gear-duotone text-lg" />
          </button>

          {/* Logout button - only show if user is logged in */}
          {userData.email && (
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 cursor-pointer hover:bg-red-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border-2 border-red-700/50 shadow-md whitespace-nowrap font-semibold"
            >
              <LogOut className="w-4 h-4" />
              {t('navigation.logout')}
            </button>
          )}
        </div>

        {/* Mobile version - show only hamburger menu */}
        <div className="flex md:hidden gap-2 items-center relative" ref={mobileMenuRef}>
          {/* Hamburger menu button */}
          {!chat.started && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="flex items-center justify-center cursor-pointer hover:bg-alpha-elements-button-secondary-backgroundHover text-alpha-elements-textPrimary w-10 h-10 rounded-full transition-all duration-200 border border-alpha-elements-borderColor shadow-sm bg-alpha-elements-button-secondary-background"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
            </button>
          )}

          {chat.started && isMobile() && (
            <button
              onClick={() => chatStore.setKey('showChat', !chat.showChat)}
              className="z-[2000] flex items-center justify-center cursor-pointer bg-white text-black px-3 py-1 rounded-md transition-all duration-200 border border-gray-200 shadow-sm hover:bg-gray-100 mr-4"
            >
              {chat.showChat ? t('header.preview') : t('header.chat')}
            </button>
          )}

          {/* Mobile dropdown menu */}
          {isMobileMenuOpen && !chat.started && (
            <div className="absolute top-full right-0 mt-2 w-64 bg-alpha-elements-background-depth-2 border border-alpha-elements-borderColor rounded-lg shadow-xl shadow-alpha-elements-sidebar-dropdownShadow backdrop-blur-sm z-50">
              <div className="py-1">
                {/* Model name - only show in dev */}
                {isDev && (
                  <div className="px-4 py-3 text-sm text-alpha-elements-textSecondary border-b border-alpha-elements-borderColor">
                    Model: {model}
                  </div>
                )}

                {/* Subscription Status / Upgrade button */}
                {userData.email !== 'user_email@dummy.dualite' &&
                  window.location.pathname !== '/billing' &&
                  !window.location.pathname.includes('/chat') && (
                    <div className="px-4 py-3 border-b border-alpha-elements-borderColor">
                      {userData.isSubscribed && areAllPromptsUsed ? (
                        <button
                          className="w-full cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-lg text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md font-semibold"
                          onClick={() => {
                            setIsProUpgradeModalOpen(true);
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          {t('header.exhausted')}
                        </button>
                      ) : userData.isSubscribed ? (
                        <div className="text-center cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-lg text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md font-semibold">
                          {t('header.planUser', { planName: planNameCleaner(userData.planName).planName })}
                        </div>
                      ) : (
                        <button
                          className="w-full cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-lg text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md font-semibold"
                          onClick={() => {
                            setIsProUpgradeModalOpen(true);
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          {t('header.upgradeToPro')}
                        </button>
                      )}
                    </div>
                  )}

                {/* Storage Management */}
                <div className="px-4 py-2 border-b border-alpha-elements-borderColor">
                  <ClientOnly>
                    {() => (
                      <div onClick={() => setIsMobileMenuOpen(false)}>
                        <StorageManagementButton setIsStorageExceededModalOpen={setIsStorageExceededModalOpen} />
                      </div>
                    )}
                  </ClientOnly>
                </div>

                {/* Tutorial */}
                <a
                  href="https://alphadocs.dualite.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-alpha-elements-item-backgroundActive transition-all duration-200 text-alpha-elements-textPrimary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <BookOpen className="w-4 h-4 text-alpha-elements-icon-secondary" />
                  Tutorial
                </a>

                {/* Support - Email Us */}
                <a
                  href="mailto:info@dualite.dev"
                  className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-alpha-elements-item-backgroundActive transition-all duration-200 text-alpha-elements-textPrimary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Mail className="w-4 h-4 text-alpha-elements-icon-secondary" />
                  {t('header.emailUs')}
                </a>

                {/* Support - Schedule Call */}
                <a
                  href="https://calendly.com/singhvirohan/dualite-feedback"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-alpha-elements-item-backgroundActive transition-all duration-200 text-alpha-elements-textPrimary border-b border-alpha-elements-borderColor"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Headset className="w-4 h-4 text-alpha-elements-icon-secondary" />
                  {t('header.scheduleCall')}
                </a>

                {/* Settings */}
                <button
                  className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-alpha-elements-item-backgroundActive transition-all duration-200 text-alpha-elements-textPrimary"
                  onClick={() => {
                    navigate('/settings');
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <i className="i-ph:gear-duotone text-lg text-alpha-elements-icon-secondary" />
                  {t('header.settings')}
                </button>

                {/* Logout - only show if user is logged in */}
                {userData.email && (
                  <div className="border-t border-alpha-elements-borderColor">
                    <button
                      className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-alpha-elements-item-backgroundDanger transition-all duration-200 text-alpha-elements-item-contentDanger"
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <LogOut className="w-4 h-4" />
                      {t('navigation.logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <ProUpgradeModal
        isOpen={isProUpgradeModalOpen}
        onClose={() => setIsProUpgradeModalOpen(false)}
        isTopUp={userData.isSubscribed && areAllPromptsUsed}
        paymentUrl={paymentUrl}
      />

      <StorageExceededModal isOpen={isStorageExceededModalOpen} onClose={() => setIsStorageExceededModalOpen(false)} />

      {/* <div className="ml-6 w-fit text-sm text-alpha-elements-textSecondary">
        <IssueDropdown framework="react" />
      </div> */}
      {/* {chat.started && (
        <ClientOnly>
          {() => (
            <div className="mr-1">
              <HeaderActionButtons />
            </div>
          )}
        </ClientOnly>
      )} */}
      {/* {chat.started && <RightTopBar />} */}

      {/* <button
        onClick={() => setIsDeployModalOpen(true)}
        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
      >
      </button> */}

      {/* <DeployModal
        isOpen={isDeployModalOpen}
        onClose={() => setIsDeployModalOpen(false)}
        accessToken="YOUR_OAUTH_TOKEN" // TODO: Get this from OAuth flow
      /> */}
    </header>
  );
}
